<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import ThreeColumn from "@/components/layout/ThreeColumn.vue";
import {
	extractUtterances,
	extractionRows,
	type ExtractionResult,
	knowledgeAttributeParts,
	type Prompt,
	sessionTreeRows,
	type TriState,
} from "@/lib/extraction";
import { promptLabel } from "@/lib/promptLabels";
import { createPromptSpeaker } from "@/lib/promptSpeech";
import { playAcknowledgement, primeAudio } from "@/lib/sound";
import {
	buildTranscriptLines,
	emptyTranscriptionState,
	formatPauseDuration,
	parseServerMessage,
	rawTranscript,
	reduceTranscription,
	startMicCapture,
	transcriptFromLines,
	utteranceSegments,
	type MicCapture,
	type TranscriptBox,
	type TranscriptionState,
} from "@/lib/voice";

/**
 * /_/voice — a minimal live speech-to-text widget. Captures the microphone,
 * streams it to the server's /api/audio/transcription Deepgram proxy, and shows
 * the live (interim + committed) transcript. As utterance segments commit they
 * are sent to /api/audio/extraction (the iOS extraction-engine port), whose data
 * extractions + knowledge graph are rendered below the transcript so new ideas
 * for the iOS app can be tested quickly from the browser.
 */

const recording = ref(false);
const transcript = ref<TranscriptionState>(emptyTranscriptionState());
const error = ref<string | null>(null);

// Extraction panel state.
const extraction = ref<ExtractionResult | null>(null);
const extractionError = ref<string | null>(null);

// Session tree (sessionTree) flattened depth-first into indented debug rows —
// the hierarchical rendering of the knowledge graph.
const treeRows = computed(() =>
	sessionTreeRows(extraction.value?.sessionTree?.root ?? null),
);

// Knowledge-level inference over the session tree (S3): the derived
// understanding read, prompt-worthy gaps, and facts implied by the tree.
const inference = computed(() => extraction.value?.inference ?? null);

// Procedural proposals derived from the knowledge graph (S4): inventory
// changes, follow-up visits, and the reason each effect was proposed.
const procedural = computed(() => extraction.value?.procedural ?? null);

// Ordered symbolic prompts over the session tree + inference (S5): index 0 is
// the prompt to speak next; the rest are lower-priority reminders.
const prompts = computed<Prompt[]>(
	() => extraction.value?.prompts?.prompts ?? [],
);

// Headset mode's spoken prompt: the prompt to act on next (index 0). It is
// voiced when a pause is detected; its symbolic situation token identifies the
// audio, which the speaker fetches ahead of the pause so speech starts on time.
const headPrompt = computed<Prompt | null>(() => prompts.value[0] ?? null);
const promptSpeechError = ref<string | null>(null);
const promptSpeech = createPromptSpeaker({
	onError: (message) => {
		promptSpeechError.value = message;
	},
});
watch(
	() => headPrompt.value?.situation,
	(situation) => {
		promptSpeechError.value = null;
		void promptSpeech.select(situation);
	},
	{ immediate: true },
);

/** Compact symbol for a tri-state knowledge-view state (debug rendering). */
function triStateSymbol(state: TriState): string {
	return state === "present" ? "✓" : state === "notPresent" ? "✕" : "?";
}

// Microphone input-level meter state.
const inputLevel = ref(0);
const clipping = ref(false);
const levelPercent = computed(() => Math.round(inputLevel.value * 100));
const levelZone = computed(() => {
	if (clipping.value || inputLevel.value > 0.85) {
		return "loud";
	}
	if (inputLevel.value < 0.2) {
		return "quiet";
	}
	return "good";
});
const levelHint = computed(() =>
	levelZone.value === "loud"
		? "Too loud"
		: levelZone.value === "quiet"
			? "Too quiet"
			: "Good level",
);

// Headset mode: emit audio acknowledgements and prompt the user during quiet
// moments. For now the prompt is a visual indicator (no audio yet) shown when a
// pause is detected, plus a blip when a new extraction appears.
const headsetMode = ref(false);
const wouldPrompt = ref(false);

let ws: WebSocket | null = null;
let mic: MicCapture | null = null;
let extractTimer: number | undefined;
// Identifies the newest extraction request so a slow older response cannot
// overwrite the panel (manual entry makes overlapping requests routine).
let extractRequest = 0;
// Extraction kinds already announced this recording (one record per kind).
let seenExtractionKinds = new Set<string>();

// Manual transcript entry: lets a known transcript be rendered exactly, for
// reproducing a state without having to speak it.
const manualOpen = ref(false);
const manualText = ref("");
let copiedTimer: number | undefined;
const copied = ref(false);

// Per-utterance transcript lines, each split into token spans carrying the
// extractions whose trigger words cover them; plus the in-flight interim text.
const lines = computed(() =>
	buildTranscriptLines(transcript.value, extraction.value),
);
const interim = computed(() => transcript.value.interim);
const hasTranscript = computed(
	() => rawTranscript(transcript.value).length > 0,
);

// Section grouping: which of the four layers has anything to show. Each
// section renders its content when it has some and a placeholder otherwise.
// Extractions flatten into one table, ordered by where each was said.
const rows = computed(() => extractionRows(extraction.value));
const hasKnowledgeGraph = computed(() => treeRows.value.length > 0);
const hasInference = computed(
	() => inference.value !== null || procedural.value !== null,
);

// Custom tags captured from the transcript ("tag as <X>"), in capture order.
const tags = computed(() => extraction.value?.tags ?? []);

/** Copy the transcript as utterance-per-line text, ready to paste back in. */
async function copyTranscript() {
	const text = rawTranscript(transcript.value);
	if (text.length === 0) {
		return;
	}
	try {
		await navigator.clipboard.writeText(text);
		copied.value = true;
		window.clearTimeout(copiedTimer);
		copiedTimer = window.setTimeout(() => {
			copied.value = false;
		}, 1500);
	} catch (e) {
		error.value =
			e instanceof Error
				? `Could not copy the transcript: ${e.message}`
				: "Could not copy the transcript";
	}
}

/**
 * Replace the live transcript with the entered text and re-run extraction, so
 * the transcript, boxes and panels all render the entered utterances.
 */
function applyManualTranscript() {
	if (manualText.value.trim().length === 0) {
		return;
	}
	if (recording.value) {
		stop();
	}
	transcript.value = transcriptFromLines(manualText.value);
	manualOpen.value = false;
	clearExtractTimer();
	void runExtract();
}

/** Screen-reader description of the extractions covering a token span. */
function boxAriaLabel(boxes: TranscriptBox[]): string | undefined {
	if (boxes.length === 0) {
		return undefined;
	}
	return boxes
		.map((box) => {
			const detail = box.summary ? ` = ${box.summary}` : "";
			return `${box.kind} ${box.label}${detail}`;
		})
		.join("; ");
}

function applyServerMessage(raw: string) {
	const message = parseServerMessage(raw);
	if (!message) {
		return;
	}
	// Every frame feeds the reducer: utterance_end closes the segment and
	// speech_started supplies the pause bound used to measure silence.
	transcript.value = reduceTranscription(transcript.value, message);
	if (message.type === "speech_started") {
		// Speech resumed; the system would stop prompting, and a prompt that has
		// not been heard yet is no longer wanted.
		wouldPrompt.value = false;
		promptSpeech.cancel();
		return;
	}
	if (message.type === "utterance_end") {
		// A pause was detected; in headset mode the system prompts now.
		wouldPrompt.value = headsetMode.value;
		if (headsetMode.value) {
			promptSpeech.speak();
		}
		return;
	}
	if (message.type === "results" && message.isFinal) {
		scheduleExtract();
	} else if (message.type === "error") {
		error.value = message.message;
		stop();
	}
}

async function runExtract() {
	const segments = utteranceSegments(transcript.value)
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
	if (segments.length === 0) {
		extraction.value = null;
		return;
	}
	extractionError.value = null;
	// Manual entry and rapid edits can put several requests in flight; only the
	// newest one may write to the panel, or a slow older response would render
	// the previous transcript's extraction under the current text.
	const request = ++extractRequest;
	try {
		const result = await extractUtterances(segments);
		if (request !== extractRequest) {
			return;
		}
		extraction.value = result;

		// Acknowledge a newly-appeared extraction kind in headset mode.
		const newKinds = result.extractions
			.map((record) => record.kind)
			.filter((kind) => !seenExtractionKinds.has(kind));
		for (const kind of newKinds) {
			seenExtractionKinds.add(kind);
		}
		if (newKinds.length > 0 && headsetMode.value) {
			playAcknowledgement();
		}
	} catch (e) {
		if (request === extractRequest) {
			extractionError.value =
				e instanceof Error ? e.message : "Extraction request failed";
		}
	}
}

/** Debounce extraction so it does not fire on every interim result. */
function scheduleExtract() {
	window.clearTimeout(extractTimer);
	extractTimer = window.setTimeout(() => {
		void runExtract();
	}, 400);
}

function clearExtractTimer() {
	if (extractTimer !== undefined) {
		window.clearTimeout(extractTimer);
		extractTimer = undefined;
	}
}

function transcriptionUrl(): string {
	const protocol = window.location.protocol === "https:" ? "wss" : "ws";
	return `${protocol}://${window.location.host}/api/audio/transcription`;
}

function closeSocket() {
	if (ws) {
		const socket = ws;
		ws = null;
		if (
			socket.readyState === WebSocket.OPEN ||
			socket.readyState === WebSocket.CONNECTING
		) {
			socket.close();
		}
	}
}

function stop() {
	if (mic) {
		mic.stop();
		mic = null;
	}
	closeSocket();
	clearExtractTimer();
	recording.value = false;
	inputLevel.value = 0;
	clipping.value = false;
	wouldPrompt.value = false;
	promptSpeech.stop();
	promptSpeech.resetSession();
	// Process whatever has been transcribed so far (e.g. a final that never
	// arrived as the user released the button).
	void runExtract();
}

function toggleHeadsetMode() {
	headsetMode.value = !headsetMode.value;
	if (headsetMode.value) {
		// Unlock audio playback from this user gesture.
		primeAudio();
	} else {
		wouldPrompt.value = false;
		promptSpeech.stop();
	}
}

async function start() {
	if (recording.value) {
		return;
	}
	transcript.value = emptyTranscriptionState();
	error.value = null;
	extraction.value = null;
	extractionError.value = null;
	inputLevel.value = 0;
	clipping.value = false;
	seenExtractionKinds = new Set<string>();
	wouldPrompt.value = false;
	// A new recording is a new session: its prompts are asked again.
	promptSpeech.resetSession();
	recording.value = true;

	const socket = new WebSocket(transcriptionUrl());
	socket.binaryType = "arraybuffer";
	try {
		await new Promise<void>((resolve, reject) => {
			socket.onopen = () => resolve();
			socket.onerror = () =>
				reject(new Error("Could not connect to the transcription service"));
		});
	} catch (e) {
		error.value =
			e instanceof Error
				? e.message
				: "Could not connect to the transcription service";
		stop();
		return;
	}

	ws = socket;
	socket.onmessage = (event) => {
		if (typeof event.data === "string") {
			applyServerMessage(event.data);
		}
	};
	socket.onclose = () => {
		// Defensive: if the socket dies (e.g. Deepgram session ended), stop.
		stop();
	};

	try {
		mic = await startMicCapture({
			onAudio: (bytes) => {
				if (ws && ws.readyState === WebSocket.OPEN) {
					ws.send(bytes);
				}
			},
			onLevel: (level, isClipping) => {
				// Fast attack, slow release so the bar tracks speech naturally.
				inputLevel.value = Math.max(level, inputLevel.value * 0.7);
				clipping.value = isClipping;
			},
		});
	} catch (e) {
		error.value =
			e instanceof Error ? e.message : "Could not access the microphone";
		stop();
	}
}

onBeforeUnmount(() => {
	clearExtractTimer();
	window.clearTimeout(copiedTimer);
	stop();
});
</script>

<template>
	<ThreeColumn two-column-split="40-60">
		<template #header>
			<div class="toolbar" role="toolbar" aria-label="Transcription controls">
				<h1>Voice Transcription</h1>
				<div class="toolbar-actions">
					<button
						type="button"
						class="icon-button"
						:disabled="recording"
						aria-label="Start recording"
						title="Start recording"
						@click="start"
					>
						<i class="bi bi-mic-fill" aria-hidden="true"></i>
					</button>
					<button
						type="button"
						class="icon-button"
						:disabled="!recording"
						aria-label="Stop recording"
						title="Stop recording"
						@click="stop"
					>
						<i class="bi bi-stop-fill" aria-hidden="true"></i>
					</button>
					<button
						type="button"
						class="icon-button"
						:class="{ active: headsetMode }"
						:aria-pressed="headsetMode"
						aria-label="Headset mode"
						title="Headset mode"
						@click="toggleHeadsetMode"
					>
						<i class="bi bi-headset" aria-hidden="true"></i>
					</button>
					<button
						type="button"
						class="icon-button"
						:class="{ active: manualOpen }"
						:aria-pressed="manualOpen"
						aria-label="Enter a transcript as text"
						title="Enter a transcript as text"
						@click="manualOpen = !manualOpen"
					>
						<i class="bi bi-keyboard" aria-hidden="true"></i>
					</button>
					<button
						type="button"
						class="icon-button"
						:disabled="!hasTranscript"
						:aria-label="copied ? 'Transcript copied' : 'Copy transcript'"
						:title="copied ? 'Copied' : 'Copy transcript'"
						@click="copyTranscript"
					>
						<i
							:class="copied ? 'bi bi-clipboard-check' : 'bi bi-clipboard'"
							aria-hidden="true"
						></i>
					</button>
					<a
						class="icon-button"
						href="/_/voice/help"
						target="_blank"
						rel="noopener"
						aria-label="Voice guide"
						title="Voice guide"
					>
						<i class="bi bi-question-circle" aria-hidden="true"></i>
					</a>
					<span class="context-badge" role="status">
						Context: {{ extraction?.currentContext ?? "—" }}
					</span>
					<span class="status" :class="{ recording }">
						{{ recording ? "Recording…" : "Idle" }}
					</span>
				</div>
			</div>
		</template>

		<template #left>
			<div class="column-stack">
				<div v-if="manualOpen" class="manual-entry">
					<label class="manual-label" for="manual-transcript">
						Transcript — one utterance per line
					</label>
					<textarea
						id="manual-transcript"
						v-model="manualText"
						class="manual-text"
						rows="6"
						placeholder="begin site&#10;land use residential begin feature habitat swimming pool"
					></textarea>
					<p class="hint">
						Replaces the live transcript so a known state can be reproduced.
						Pauses come from Deepgram, so they are not reproduced here.
					</p>
					<div class="manual-actions">
						<button
							type="button"
							:disabled="manualText.trim().length === 0"
							@click="applyManualTranscript"
						>
							Render transcript
						</button>
						<button type="button" @click="manualOpen = false">Cancel</button>
					</div>
				</div>

				<p v-if="error || extractionError" class="error">
					{{ error || extractionError }}
				</p>

				<section class="layer">
					<div v-if="recording" class="input-level">
						<div
							class="level-meter"
							role="img"
							:aria-label="`Microphone level ${levelPercent}%: ${levelHint}`"
						>
							<div class="level-band"></div>
							<div
								class="level-fill"
								:class="levelZone"
								:style="{ width: `${levelPercent}%` }"
							></div>
						</div>
						<span class="level-hint" :class="levelZone">{{ levelHint }}</span>
					</div>

					<div
						v-if="recording && headsetMode && wouldPrompt"
						class="prompt-badge"
						role="status"
					>
						prompt
					</div>

					<span
						v-if="recording && headsetMode && promptSpeechError"
						class="prompt-error"
						role="alert"
					>
						{{ promptSpeechError }}
					</span>

					<div class="layer-body">
						<div class="transcript" role="log" aria-label="Live transcription">
							<p v-if="lines.length === 0 && !interim" class="hint">
								Your live transcription will appear here.
							</p>
							<template v-for="(line, index) in lines" :key="index">
								<div
									v-if="index > 0 && line.pauseBefore"
									class="pause-marker"
									:class="{ measured: line.pauseSeconds !== null }"
									role="separator"
								>
									{{ formatPauseDuration(line.pauseSeconds) }}
								</div>
								<p
									class="utterance"
									:class="line.context ? 'ctx-' + line.context : 'ctx-none'"
									:data-context="line.context ?? ''"
								>
									<span v-if="line.context" class="context-tag">{{
										line.context
									}}</span
									><span
										v-for="(piece, pieceIndex) in line.pieces"
										:key="pieceIndex"
										class="word"
										:class="{ boxed: piece.boxes.length > 0 }"
										:style="
											piece.hue === null
												? undefined
												: { '--box-hue': piece.hue }
										"
										:tabindex="piece.boxes.length ? 0 : undefined"
										:aria-label="boxAriaLabel(piece.boxes)"
										>{{ piece.text
										}}<span
											v-if="piece.boxes.length"
											class="box-tip"
											role="tooltip"
										>
											<template
												v-for="(box, boxIndex) in piece.boxes"
												:key="boxIndex"
											>
												<strong>{{ box.label }}</strong>
												<span class="box-kind">{{ box.kind }}</span>
												<span v-if="box.context" class="box-context">{{
													box.context
												}}</span>
												<span v-if="box.summary" class="box-summary">{{
													box.summary
												}}</span>
												<ul v-if="box.fields?.length">
													<li
														v-for="(field, fieldIndex) in box.fields"
														:key="fieldIndex"
													>
														{{ field.label }}: {{ field.value }}
													</li>
												</ul>
											</template>
										</span></span
									>
								</p>
							</template>
							<p v-if="interim" class="utterance interim">{{ interim }}</p>
						</div>
					</div>
				</section>

				<section class="layer">
					<h2 class="layer-title">Prompts</h2>
					<div class="layer-body">
						<section
							v-if="prompts.length"
							class="knowledge-view prompts"
							role="status"
						>
							<span
								v-for="(prompt, index) in prompts"
								:key="`${prompt.concept}.${prompt.attribute ?? ''}.${prompt.situation ?? ''}.${prompt.kind}.${index}`"
								class="prompt-item"
								:class="{ head: index === 0, reminder: index > 0 }"
							>
								<span class="concept-label">{{ promptLabel(prompt) }}</span>
								<span class="prompt-kind" :class="prompt.kind">{{
									prompt.kind
								}}</span>
								<span class="prompt-reason">{{ prompt.reason }}</span>
								<span v-if="prompt.source" class="prompt-source"
									>← {{ prompt.source }}</span
								>
								<span class="prompt-priority">p{{ prompt.priority }}</span>
							</span>
						</section>
						<p v-else class="hint">
							Prompts derived from the tree will appear here.
						</p>
					</div>
				</section>
			</div>
		</template>

		<template #center>
			<div class="column-stack">
				<section class="layer">
					<h2 class="layer-title">Extractions</h2>
					<div class="layer-body">
						<table v-if="rows.length" class="extraction-table">
							<thead>
								<tr>
									<th scope="col">Type</th>
									<th scope="col">Key</th>
									<th scope="col">Value</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="row in rows"
									:key="row.id"
									:class="'type-' + row.type"
									:title="
										row.context ? `${row.type} @ ${row.context}` : row.type
									"
								>
									<td class="row-type">{{ row.type }}</td>
									<td class="row-key">{{ row.key }}</td>
									<td class="row-value">{{ row.value }}</td>
								</tr>
							</tbody>
						</table>
						<p v-else class="hint">
							Extractions from the transcript will appear here.
						</p>
					</div>
				</section>

				<section class="layer">
					<h2 class="layer-title">Tags</h2>
					<div class="layer-body">
						<section
							v-if="tags.length"
							class="knowledge-view tags"
							role="status"
						>
							<span
								v-for="(tag, index) in tags"
								:key="`${index}-${tag}`"
								class="tag-item"
								>{{ tag }}</span
							>
						</section>
						<p v-else class="hint">
							Custom tags captured from the transcript will appear here.
						</p>
					</div>
				</section>

				<section class="layer">
					<h2 class="layer-title">Knowledge graph</h2>
					<div class="layer-body">
						<template v-if="hasKnowledgeGraph">
							<section
								v-if="treeRows.length"
								class="knowledge-view session-tree"
								role="status"
							>
								<span
									v-for="row in treeRows"
									:key="row.node.id"
									class="tree-row"
									:style="{ paddingLeft: `${row.depth}rem` }"
								>
									<span class="concept-header">
										<span class="concept-state" :class="row.node.state">
											{{ triStateSymbol(row.node.state) }}
										</span>
										<span class="concept-id">{{ row.node.kind }}</span>
									</span>
									<span
										v-for="attr in row.node.attributes"
										:key="`${attr.attribute}-${attr.start}`"
										class="attribute-item"
									>
										<span class="attribute-state" :class="attr.state">
											{{ triStateSymbol(attr.state) }}
										</span>
										<span class="attribute-id">{{ attr.attribute }}</span>
										<!-- One part per renderable field, decided in
										knowledgeAttributeParts so the cell cannot show a
										size attribute's amounts twice or lose them. -->
										<span
											v-for="part in knowledgeAttributeParts(attr)"
											:key="part.kind"
											:class="'attribute-' + part.kind"
											>{{ part.text }}</span
										>
									</span>
									<!-- Free-text descriptions of the node itself, one span
									each, in transcript order. -->
									<span
										v-for="(description, index) in row.node.descriptions"
										:key="`description-${index}`"
										class="description-item"
										>{{ description }}</span
									>
								</span>
							</section>
							<p v-else-if="extraction" class="hint">No session tree yet.</p>
						</template>
						<p v-else class="hint">
							The knowledge graph built from those extractions will appear here.
						</p>
					</div>
				</section>

				<section class="layer">
					<h2 class="layer-title">Inferences</h2>
					<div class="layer-body">
						<template v-if="hasInference">
							<section
								v-if="inference"
								class="knowledge-view inference"
								role="status"
							>
								<span class="inference-item">
									Understanding:
									<span class="concept-id">source</span>
									<span
										class="inference-flag"
										:class="{ present: inference.understanding.hasSource }"
										>{{ inference.understanding.hasSource }}</span
									>
									<span class="concept-id">breeding</span>
									<span
										class="inference-flag"
										:class="{ present: inference.understanding.hasBreeding }"
										>{{ inference.understanding.hasBreeding }}</span
									>
									<span class="concept-id">treatment</span>
									<span
										class="inference-flag"
										:class="{ present: inference.understanding.hasTreatment }"
										>{{ inference.understanding.hasTreatment }}</span
									>
									<span class="concept-id">open gaps</span>
									<span class="inference-flag">{{
										inference.understanding.openGaps
									}}</span>
								</span>

								<span class="inference-heading">Gaps</span>
								<span v-if="inference.gaps.length" class="inference-list">
									<span
										v-for="gap in inference.gaps"
										:key="`${gap.concept}.${gap.attribute ?? ''}.${gap.reason}`"
										class="inference-item"
									>
										<span class="concept-label">
											{{
												gap.attribute
													? `${gap.concept}.${gap.attribute}`
													: gap.concept
											}}
										</span>
										<span class="inference-reason">{{ gap.reason }}</span>
									</span>
								</span>
								<span v-else class="inference-item">No gaps.</span>

								<span class="inference-heading">Implied</span>
								<span v-if="inference.implied.length" class="inference-list">
									<span
										v-for="fact in inference.implied"
										:key="`${fact.concept}.${fact.attribute ?? ''}.${fact.reason}.${fact.source}`"
										class="inference-item"
									>
										<span class="concept-label">
											{{
												fact.attribute
													? `${fact.concept}.${fact.attribute}`
													: fact.concept
											}}
										</span>
										<span class="inference-reason">{{ fact.reason }}</span>
										<span class="inference-source">← {{ fact.source }}</span>
									</span>
								</span>
								<span v-else class="inference-item">No implied facts.</span>
							</section>
							<p v-else-if="extraction" class="hint">No inference yet.</p>

							<section
								v-if="procedural"
								class="knowledge-view procedural"
								role="status"
							>
								<span class="procedural-heading">Effects</span>
								<span v-if="procedural.effects.length" class="procedural-list">
									<span
										v-for="(effect, index) in procedural.effects"
										:key="`${effect.kind}.${effect.reason}.${effect.concept ?? ''}.${effect.product ?? ''}.${index}`"
										class="procedural-item"
									>
										<span class="concept-label">{{ effect.kind }}</span>
										<span class="concept-id">{{ effect.concept }}</span>
										<span v-if="effect.product" class="procedural-product">{{
											effect.product
										}}</span>
										<span
											v-if="effect.amount !== undefined"
											class="attribute-count"
											>{{ effect.amount }}</span
										>
										<span v-if="effect.unit" class="attribute-unit">{{
											effect.unit
										}}</span>
										<span class="procedural-reason">{{ effect.reason }}</span>
									</span>
								</span>
								<span v-else class="procedural-item"
									>No procedural effects.</span
								>
							</section>
							<p v-else-if="extraction" class="hint">
								No procedural effects yet.
							</p>
						</template>
						<p v-else class="hint">
							Inferences drawn from the knowledge graph will appear here.
						</p>
					</div>
				</section>
			</div>
		</template>
	</ThreeColumn>
</template>

<style scoped>
.toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	gap: 1rem;
	flex-wrap: wrap;
}
.toolbar h1 {
	margin: 0;
	font-size: 1.25rem;
}
.toolbar-actions {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}
.icon-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2rem;
	height: 2rem;
	padding: 0;
	font-size: 1rem;
	line-height: 1;
	border: 1px solid #aaa;
	border-radius: 999px;
	background: #fff;
	color: #333;
	cursor: pointer;
}
.icon-button:hover:not(:disabled) {
	background: #f1f5f9;
}
.icon-button:focus-visible {
	outline: 2px solid #2c7be5;
	outline-offset: 1px;
}
.icon-button:disabled {
	opacity: 0.4;
	cursor: default;
}
.icon-button.active {
	background: #1e7e34;
	border-color: #1e7e34;
	color: #fff;
}
.manual-entry {
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
	margin-bottom: 0.5rem;
	padding: 0.5rem;
	border: 1px solid #cbd5e1;
	border-radius: 6px;
	background: #f8fafc;
}
.manual-label {
	font-size: 0.85em;
	font-weight: 600;
	color: #334155;
}
.manual-text {
	width: 100%;
	box-sizing: border-box;
	padding: 0.4rem;
	border: 1px solid #cbd5e1;
	border-radius: 4px;
	font: inherit;
	resize: vertical;
}
.manual-actions {
	display: flex;
	gap: 0.5rem;
}
.manual-actions button {
	padding: 0.3rem 0.7rem;
	border: 1px solid #aaa;
	border-radius: 4px;
	background: #fff;
	font: inherit;
	cursor: pointer;
}
.manual-actions button:disabled {
	opacity: 0.5;
	cursor: default;
}
.hint {
	color: var(--text-secondary, #555);
}
.column-stack {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	height: 100%;
	min-height: 0;
}
.layer {
	display: flex;
	flex: 1 1 0;
	flex-direction: column;
	min-height: 0;
}
.layer-title {
	flex-shrink: 0;
	margin: 0 0 0.35rem;
	padding-bottom: 0.2rem;
	border-bottom: 1px solid #e2e8f0;
	color: #475569;
	font-size: 0.8rem;
	font-weight: 700;
	letter-spacing: 0.06em;
	text-transform: uppercase;
}
.layer-body {
	flex: 1 1 0;
	min-height: 0;
	overflow-y: auto;
}
.status {
	font-weight: 600;
}
.status.recording {
	color: #c0392b;
}
.input-level {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	margin: 0.5rem 0;
	flex-shrink: 0;
}
.level-meter {
	position: relative;
	flex: 1;
	height: 12px;
	background: #e5e5e5;
	border-radius: 6px;
	overflow: hidden;
}
/* Target-loudness band: the "optimum" region of the meter. */
.level-band {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 20%;
	width: 65%;
	background: rgba(40, 167, 69, 0.18);
}
.level-fill {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	border-radius: 6px;
	transition: width 80ms linear;
}
.level-fill.quiet {
	background: #f0ad4e;
}
.level-fill.good {
	background: #28a745;
}
.level-fill.loud {
	background: #d9534f;
}
.level-hint {
	font-weight: 600;
	min-width: 6rem;
}
.level-hint.quiet {
	color: #b8791a;
}
.level-hint.good {
	color: #1e7e34;
}
.level-hint.loud {
	color: #c0392b;
}
.prompt-badge {
	display: inline-block;
	margin: 0.25rem 0;
	padding: 0.2rem 0.6rem;
	border-radius: 999px;
	background: #2c7be5;
	color: #fff;
	font-size: 0.85em;
	font-weight: 600;
	letter-spacing: 0.02em;
}
.prompt-error {
	display: block;
	margin: 0.25rem 0;
	color: #c0392b;
	font-size: 0.85em;
	font-weight: 600;
}
.context-badge {
	display: inline-block;
	margin: 0;
	padding: 0.2rem 0.6rem;
	border-radius: 999px;
	background: #e2e8f0;
	color: #334155;
	font-size: 0.85em;
	font-weight: 600;
	letter-spacing: 0.02em;
}
/* Extractions: one row per extraction, in the order they were spoken. Each
   layer has its own hue so the row colour says which one produced it. */
.extraction-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.95em;
}
.extraction-table th {
	padding: 0.2rem 0.4rem;
	border-bottom: 1px solid #e2e8f0;
	color: #64748b;
	font-size: 0.75rem;
	letter-spacing: 0.05em;
	text-align: left;
	text-transform: uppercase;
}
.extraction-table td {
	padding: 0.25rem 0.4rem;
	border-bottom: 1px solid #f1f5f9;
	vertical-align: top;
}
.extraction-table .row-type {
	border-left: 3px solid hsl(var(--type-hue, 0) 55% 50%);
	background: hsl(var(--type-hue, 0) 70% 96%);
	color: #475569;
	font-size: 0.85em;
	white-space: nowrap;
}
.extraction-table .row-key {
	font-weight: 600;
}
.extraction-table .row-value {
	word-break: break-word;
}
.extraction-table tr.type-boolean {
	--type-hue: 210;
}
.extraction-table tr.type-enumeration {
	--type-hue: 145;
}
.extraction-table tr.type-number {
	--type-hue: 25;
}
.extraction-table tr.type-free-text {
	--type-hue: 265;
}
.extraction-table tr.type-negation {
	--type-hue: 0;
}
.extraction-table tr.type-meaning {
	--type-hue: 320;
}
.extraction-table tr.type-measurement {
	--type-hue: 190;
}
.extraction-table tr.type-address {
	--type-hue: 90;
}
.knowledge-view {
	margin: 0.25rem 0 0.5rem;
	font-size: 0.85em;
	color: #475569;
}
.concept-header {
	font-weight: 600;
}
.concept-id,
.attribute-id {
	margin-left: 0.35em;
	color: #94a3b8;
	font-family: ui-monospace, monospace;
	font-size: 0.9em;
	font-weight: 400;
}
.concept-state,
.attribute-state {
	margin: 0;
	padding: 0 0.35em;
	border-radius: 999px;
	font-weight: 600;
}
.concept-state.present,
.attribute-state.present {
	color: #1e7e34;
	background: rgba(30, 126, 52, 0.12);
}
.concept-state.notPresent,
.attribute-state.notPresent {
	color: #c0392b;
	background: rgba(192, 57, 43, 0.12);
}
.concept-state.unknown,
.attribute-state.unknown {
	color: #64748b;
	background: rgba(100, 116, 139, 0.12);
}
.attribute-item {
	display: block;
	margin-left: 1rem;
}
.session-tree .tree-row {
	display: block;
	margin: 0.3rem 0;
}
.inference .inference-item {
	display: block;
	margin: 0.15rem 0;
}
.inference .inference-heading {
	display: block;
	margin-top: 0.4rem;
	font-weight: 600;
}
.inference .inference-list {
	display: block;
}
.inference .inference-flag {
	margin-left: 0.35em;
	font-family: ui-monospace, monospace;
	font-size: 0.9em;
	color: #64748b;
}
.inference .inference-flag.present {
	color: #1e7e34;
	font-weight: 600;
}
.inference .inference-reason,
.inference .inference-source {
	margin-left: 0.35em;
	color: #94a3b8;
}
.procedural .procedural-item {
	display: block;
	margin: 0.15rem 0;
}
.procedural .procedural-heading {
	display: block;
	margin-top: 0.4rem;
	font-weight: 600;
}
.procedural .procedural-list {
	display: block;
}
.procedural .procedural-reason,
.procedural .procedural-product {
	margin-left: 0.35em;
	color: #94a3b8;
}
.prompts .prompt-item {
	display: block;
	margin: 0.15rem 0;
}
.prompts .prompt-item.head {
	font-weight: 600;
	color: #1e293b;
}
.prompts .prompt-item.reminder {
	color: #94a3b8;
}
.prompts .prompt-kind {
	margin-left: 0.35em;
	font-family: ui-monospace, monospace;
	font-size: 0.9em;
	color: #64748b;
}
.prompts .prompt-kind.complete {
	color: #b45309;
}
.prompts .prompt-kind.implied {
	color: #2563eb;
}
.prompts .prompt-reason,
.prompts .prompt-source,
.prompts .prompt-priority {
	margin-left: 0.35em;
	color: #94a3b8;
}
.attribute-value,
.attribute-measurement,
.attribute-count,
.attribute-unit,
.attribute-text {
	margin-left: 0.35em;
}
.attribute-text {
	font-style: italic;
}
/* A node's descriptions mirror its attributes: one indented block line each,
so several descriptions never run together on one line. */
.description-item {
	display: block;
	margin-left: 1rem;
	font-style: italic;
}
.error {
	color: #c0392b;
	font-weight: 600;
}
.transcript {
	width: 100%;
	box-sizing: border-box;
	padding: 0.75rem;
	font: inherit;
	line-height: 1.5;
}
.utterance {
	display: block;
	margin: 0.15rem 0;
	padding: 0.2rem 0.45rem;
	border-left: 3px solid transparent;
	border-radius: 4px;
	white-space: pre-wrap;
	word-break: break-word;
}
.utterance.ctx-access {
	background: #e3f2fd;
	border-left-color: #1e88e5;
}
.utterance.ctx-facilitator {
	background: #f3e5f5;
	border-left-color: #8e24aa;
}
.utterance.ctx-feature {
	background: #e8f5e9;
	border-left-color: #2e7d32;
}
.utterance.ctx-inspection {
	background: #fff8e1;
	border-left-color: #f9a825;
}
.utterance.ctx-mitigator {
	background: #fce4ec;
	border-left-color: #c2185b;
}
.utterance.ctx-safety {
	background: #e0f7fa;
	border-left-color: #00838f;
}
.utterance.ctx-site {
	background: #efebe9;
	border-left-color: #6d4c41;
}
.utterance.ctx-treatment {
	background: #ede7f6;
	border-left-color: #5e35b1;
}
.utterance.ctx-none {
	background: #f8fafc;
	border-left-color: #cbd5e1;
}
.utterance.interim {
	font-style: italic;
	color: #94a3b8;
}
.tags .tag-item {
	display: inline-block;
	margin: 0.1rem 0.35rem 0.1rem 0;
	padding: 0.05rem 0.5rem;
	border-radius: 999px;
	background: rgba(100, 116, 139, 0.14);
	color: #334155;
	font-size: 0.85em;
}

.context-tag {
	display: inline-block;
	margin-right: 0.4em;
	font-size: 0.7em;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #475569;
	opacity: 0.85;
}
.word {
	position: relative;
}
.word.boxed {
	background: hsl(var(--box-hue, 45) 85% 92%);
	border: 1px solid hsl(var(--box-hue, 45) 55% 45%);
	border-radius: 3px;
	padding: 0 0.12em;
	box-decoration-break: clone;
}
.word.boxed:focus {
	outline: 2px solid #0f172a;
	outline-offset: 1px;
}
.box-tip {
	position: absolute;
	top: 100%;
	left: 0;
	z-index: 5;
	display: none;
	max-width: 26rem;
	padding: 0.4rem 0.55rem;
	background: #fff;
	border: 1px solid #cbd5e1;
	border-radius: 4px;
	box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
	white-space: normal;
	color: #1e293b;
	font-size: 0.78rem;
	font-weight: 400;
	line-height: 1.35;
	letter-spacing: normal;
	text-align: left;
	text-transform: none;
}
.word.boxed:hover .box-tip,
.word.boxed:focus .box-tip,
.word.boxed:focus-within .box-tip {
	display: block;
}
.box-tip strong {
	display: block;
}
.box-kind {
	display: inline-block;
	margin-left: 0.35em;
	color: #64748b;
	font-size: 0.85em;
}
.box-context {
	display: inline-block;
	margin-left: 0.35em;
	color: #94a3b8;
	font-size: 0.85em;
}
.box-context::before {
	content: "@ ";
}
.box-summary {
	display: block;
	color: #334155;
}
.box-tip ul {
	list-style: none;
	margin: 0.25rem 0 0;
	padding: 0;
}
.box-tip li {
	margin: 0.1rem 0;
}
.pause-marker {
	display: flex;
	align-items: center;
	margin: 0.35rem 0;
	color: #64748b;
	font-size: 0.8em;
	white-space: nowrap;
}
.pause-marker::before,
.pause-marker::after {
	content: "";
	flex: 1;
	border-top: 1px dashed currentColor;
	margin: 0 0.5rem;
}
.pause-marker.measured {
	color: #475569;
	font-weight: 600;
}
</style>
